'use client';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react';
import Link from 'next/link';

const buttonClasses = cva(
  'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary: 'bg-button-background text-button-text hover:bg-primary-background focus:ring-primary-background',
        secondary: 'bg-secondary-background text-secondary-foreground hover:bg-secondary-light focus:ring-secondary-foreground',
        outline: 'border-2 border-border-primary text-text-primary bg-transparent hover:bg-secondary-light focus:ring-border-primary',
        danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
      },
      size: {
        small: 'text-xs px-3 py-1.5',
        medium: 'text-sm px-4 py-2',
        large: 'text-md px-6 py-3',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  }
);

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonClasses> {
  text?: string;
  children?: ReactNode;
  href?: string; // <-- Add this prop for navigation
  text_font_size?: string;
  text_font_family?: string;
  text_font_weight?: string;
  text_line_height?: string;
  text_letter_spacing?: string;
  text_text_align?: string;
  text_text_transform?: string;
  text_color?: string;
  border_border?: string;
  border_border_radius?: string;
  fill_background_color?: string;
  layout_gap?: string;
  layout_width?: string;
  padding?: string;
  position?: string;
  margin?: string;
}

const Button = ({
  text = "Contact us",
  href,
  text_font_size = "text-xs",
  text_font_family = "Oxanium",
  text_font_weight = "font-medium",
  text_line_height = "leading-tight",
  text_letter_spacing = "tracking-[2px]",
  text_text_align = "center",
  text_text_transform = "uppercase",
  text_color = "text-text-primary",
  border_border = "1px solid #ffffff",
  border_border_radius = "rounded-xs",
  fill_background_color,
  layout_gap,
  layout_width,
  padding,
  position,
  margin,
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = 'button',
  ...props
}: ButtonProps) => {

  const hasValidBackgroundColor = fill_background_color && fill_background_color.trim() !== ''
  const optionalClasses = [
    hasValidBackgroundColor ? `bg-[${fill_background_color}]` : '',
    layout_gap ? `gap-[${layout_gap}]` : '',
    layout_width ? `w-[${layout_width}]` : '',
    padding ? `p-[${padding}]` : '',
    margin ? `m-[${margin}]` : '',
    position || '',
  ].filter(Boolean).join(' ')

  const customStyles: CSSProperties = {
    ...(text_font_family && !text_font_family.startsWith('font-') && { fontFamily: text_font_family }),
  }

  const styleClasses = [
    text_font_size,
    text_font_family.startsWith('font-') ? text_font_family : '',
    text_font_weight,
    text_line_height,
    text_letter_spacing,
    `text-${text_text_align}`,
    text_text_transform,
    text_color,
    !variant && !hasValidBackgroundColor ? 'bg-transparent' : '',
    !variant ? `border-[${border_border}]` : '',
    border_border_radius,
  ].filter(Boolean).join(' ')

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      event.preventDefault()
      return
    }
    
    if (typeof onClick === 'function') {
      onClick(event)
    }
  }

  // Render as Link if href exists
  if (href) {
    return (
      <Link data-component-id="src/components/ui/Button.tsx:123:6" data-component-path="src/components/ui/Button.tsx" data-component-line="123" data-component-end-line="133" data-component-file="Button.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%5Bvar%3Ahref%5D%22%7D"
        href={href}
        className={twMerge(
          buttonClasses({ variant, size }),
          styleClasses,
          optionalClasses,
          className
        )}
      >
        {children || text}
      </Link>
    )
  }

  // Otherwise render as button
  return (
    <button data-component-id="src/components/ui/Button.tsx:139:4" data-component-path="src/components/ui/Button.tsx" data-component-line="139" data-component-end-line="153" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={customStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        styleClasses,
        optionalClasses,
        className
      )}
      {...props}
    >
      {children || text}
    </button>
  )
}

export default Button;
